import { Chip, CircularProgress, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import matchingRoundsAPI, {
  MatchRecommendation,
} from '../../../api/matching-rounds';
import mentorsAPI, { Mentor, MentorId } from '../../../api/mentors';
import { VentureId } from '../../../api/ventures/types/Venture';
import { useResourceBundles } from '../../../contexts/resource-bundles-context';
import { isMobile, removeDuplicates } from '../../../utils/functions';
import MentorAutocomplete from '../../autocomplete/mentor-autocomplete';
import {
  AssignmentRecommendedTable,
  Button,
  FormButtons,
  FormGroup,
  TextField,
} from '../../common';
import ModalV2 from '../../common/modal-v2';

interface RecommendedMentor extends Mentor {
  ai?: boolean;
}

interface HeatmapAssignMentorModalProps {
  open: boolean;
  onClose: () => void;
  ventureId: VentureId;
  ventureName: string;
  matchId: string;
  existingMentorIds: MentorId[];
  onRecommendationCreated: (rec: MatchRecommendation) => void;
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 285,
    height: '100%',
  },
  formButtons: {
    alignItems: 'flex-end',
    flexGrow: 1,
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  chips: {
    display: 'flex',
    alignItems: 'center',
    flexFlow: 'row wrap',
    margin: '15px 0 0 -10px',
  },
  chipItem: {
    margin: '0 0 10px 10px',
    overflow: 'hidden',
    maxWidth: '100%',

    [theme.breakpoints.up('sm')]: {
      overflow: 'initial',
    },
  },
}));

function HeatmapAssignMentorModal({
  open,
  onClose,
  ventureId,
  ventureName,
  matchId,
  existingMentorIds,
  onRecommendationCreated,
}: HeatmapAssignMentorModalProps) {
  const classes = useStyles();
  const { rb } = useResourceBundles();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedMentors, setSelectedMentors] = useState<Mentor[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchIteration, setSearchIteration] = useState(0);
  const [displayedMentors, setDisplayedMentors] = useState<RecommendedMentor[]>(
    [],
  );
  const [recommended, setRecommended] = useState<RecommendedMentor[]>([]);
  const [isLoadingRecommended, setIsLoadingRecommended] = useState(true);

  useEffect(() => {
    if (!open) return;
    // Reset state when modal opens
    setSelectedMentors([]);
    setSearchIteration(0);
    setDisplayedMentors([]);
    setRecommended([]);
    setIsLoadingRecommended(true);

    let cancelled = false;

    const loadRecommended = async () => {
      try {
        const [loadedFromAI, loaded, assignedMentors] = await Promise.all([
          mentorsAPI.getMentorsRecommendedByAIForVenture(ventureId),
          mentorsAPI.getMentorsRecommendedForVenture(ventureId),
          mentorsAPI.getMentorsByVentureId(ventureId),
        ]);
        if (cancelled) return;
        const assignedIds = new Set(
          assignedMentors.map((m) => m.mentorId),
        );
        const filteredAI = loadedFromAI.filter(
          (m) => !assignedIds.has(m.id),
        );
        const merged = [
          ...filteredAI.map((m) => ({ ...m, ai: true })),
          ...loaded,
        ];
        setRecommended(removeDuplicates(merged));
      } catch (_e) {
        // Recommendations are optional — ignore errors
      } finally {
        if (!cancelled) {
          setIsLoadingRecommended(false);
        }
      }
    };

    loadRecommended();
    return () => {
      cancelled = true;
    };
  }, [open, ventureId]);

  const handleMentorsSearch = useCallback((mentors: Mentor[]) => {
    setDisplayedMentors(mentors);
  }, []);

  const handleMentorAdd = useCallback((mentor: Mentor) => {
    setSelectedMentors((prev) => [...prev, mentor]);
    setSearchIteration((prev) => prev + 1);
  }, []);

  const handleMentorRemove = useCallback((mentor: Mentor) => {
    setSelectedMentors((prev) => prev.filter((m) => m.id !== mentor.id));
  }, []);

  const handleApply = useCallback(async () => {
    if (selectedMentors.length === 0) return;
    setIsSubmitting(true);

    let successCount = 0;

    for (const mentor of selectedMentors) {
      try {
        const rec = await matchingRoundsAPI.createRecommendation(matchId, {
          mentorId: mentor.id,
        });
        // Enrich with mentor details (backend only returns id, mentorId, weight)
        const enrichedRec: MatchRecommendation = {
          ...rec,
          mentorName: `${mentor.firstName} ${mentor.lastName}`,
          specialization: '',
          about: mentor.about || '',
        };
        onRecommendationCreated(enrichedRec);
        successCount++;
      } catch (e: unknown) {
        const msg =
          (e as AxiosError)?.response?.data?.message ||
          'Failed to create recommendation';
        enqueueSnackbar(
          `Failed to assign ${mentor.firstName} ${mentor.lastName}`,
          { variant: 'error' },
        );
        console.error('Failed to create recommendation:', msg);
      }
    }

    setIsSubmitting(false);
    if (successCount > 0) {
      onClose();
    }
  }, [
    selectedMentors,
    matchId,
    onRecommendationCreated,
    onClose,
    enqueueSnackbar,
  ]);

  const allExcludedIds = useMemo(
    () => new Set([...existingMentorIds, ...selectedMentors.map((m) => m.id)]),
    [existingMentorIds, selectedMentors],
  );

  const filteredDisplayed = displayedMentors.filter(
    (m) => !allExcludedIds.has(m.id),
  );
  const filteredRecommended = recommended.filter(
    (m) => !allExcludedIds.has(m.id),
  );
  const displayedList =
    filteredDisplayed.length > 0 ? filteredDisplayed : filteredRecommended;

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      width='720px'
      titleProps={{
        title: 'New assignment',
        caption: ventureName,
      }}
    >
      <div className={classes.form} data-testid='heatmap-assign-mentor-form'>
        <FormGroup>
          <MentorAutocomplete
            iteration={searchIteration}
            onSearch={handleMentorsSearch}
            ventureId={ventureId}
            inputRenderer={({ value, onChange, isLoading }) => (
              <TextField
                value={value}
                label='Assign to'
                placeholder={`Enter ${rb('mentor')} name`}
                onChange={onChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: isLoading && (
                    <CircularProgress color='primary' size={20} />
                  ),
                }}
              />
            )}
          />
        </FormGroup>
        <div className={classes.chips}>
          {selectedMentors.map((mentor) => (
            <Chip
              key={mentor.id}
              label={`${mentor.firstName} ${mentor.lastName}`}
              onDelete={() => handleMentorRemove(mentor)}
              className={classes.chipItem}
              data-testid={`selected-mentor-${mentor.id}`}
            />
          ))}
        </div>
        {isLoadingRecommended ? (
          <div className={classes.loader}>
            <CircularProgress size={24} color='primary' />
          </div>
        ) : (
          displayedList.length > 0 && (
            <AssignmentRecommendedTable
              labels={[
                rb('mentors-u'),
                isMobile() ? 'Assigned' : 'Assigned Ventures',
              ]}
              rows={displayedList.map((recomItem) => ({
                name: `${recomItem.firstName} ${recomItem.lastName}`,
                assigned: recomItem.assigned,
                interested: recomItem.voted,
                ai: recomItem.ai,
                onAdd: () => handleMentorAdd(recomItem),
              }))}
              mobileFullHeight
            />
          )
        )}
        <FormButtons className={classes.formButtons}>
          <Button
            onClick={onClose}
            variant='outlined'
            data-testid='heatmap-assign-mentor-cancel'
          >
            Cancel
          </Button>
          <Button
            disabled={selectedMentors.length === 0 || isSubmitting}
            onClick={handleApply}
            data-testid='heatmap-assign-mentor-apply'
          >
            {isSubmitting ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Apply'
            )}
          </Button>
        </FormButtons>
      </div>
    </ModalV2>
  );
}

export default HeatmapAssignMentorModal;
